import React from "react";
import PropTypes from "prop-types";
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Box, Button } from '@mui/material';
import * as XLSX from 'xlsx';
import { getClientTheme } from "../../theme/clientTheme";
import { useData } from "../../context/DataContext";

const MaterialReactTableWithSearch = ({
    columns,
    data,
    enableRowActions,
    renderRowActions,
    name,
    enableRowSelection,
    renderTopToolbarCustomActions,
    enableSelectAll,
    onExportToExcel, // Add the onExportToExcel prop
    manualPagination,          // enable server-side pagination
    rowCount,                  // total record count from the API
    pagination,                // { pageIndex, pageSize } (controlled)
    onPaginationChange,        // fires with the next { pageIndex, pageSize }
    isLoading,                 // shows the shimmer/skeleton loader
}) => {
    const { user_client_id } = useData();
    const clientTheme = getClientTheme(user_client_id);

    // server-side pagination options (only applied when manualPagination is set)
    const manualPaginationProps = manualPagination
        ? {
            manualPagination: true,
            rowCount: rowCount ?? 0,
            onPaginationChange: (updater) => {
                const current = pagination ?? { pageIndex: 0, pageSize: 10 };
                const next = typeof updater === "function" ? updater(current) : updater;
                if (onPaginationChange) onPaginationChange(next);
            },
        }
        : {};

    // controlled state: pagination (server mode) + isLoading (shimmer)
    const controlledState = {
        ...(manualPagination ? { pagination: pagination ?? { pageIndex: 0, pageSize: 10 } } : {}),
        ...(isLoading != null ? { isLoading } : {}),
    };

    const table = useMaterialReactTable({
        columns,
        data,
        ...manualPaginationProps,
        ...(Object.keys(controlledState).length ? { state: controlledState } : {}),
        enableSelectAll,
        renderTopToolbarCustomActions,
        enableRowSelection,
        enableStickyHeader: false,
        enableColumnFilterModes: false,
        enableColumnOrdering: false,
        enableGrouping: false,
        enableColumnPinning: false,
        enableFacetedValues: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableColumnActions: false,
        enableRowActions,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        enableGlobalFilter: true,
        muiTableContainerProps: { sx: { maxHeight: 'auto' } },
        initialState: {
            showColumnFilters: false,
            showGlobalFilter: false,
            columnPinning: {
                left: ['mrt-row-expand', 'mrt-row-select'],
                right: ['mrt-row-actions'],
            },
        },
        renderRowActions,
        paginationDisplayMode: 'pages',
        positionToolbarAlertBanner: 'bottom',
        muiSearchTextFieldProps: {
            size: 'small',
            variant: 'outlined',
        },
        muiPaginationProps: {
            color: 'secondary',
            rowsPerPageOptions: [10, 50, 100, 200],
            shape: 'rounded',
            variant: 'outlined',
        },
        muiTableBodyRowProps: {
            sx: {
                height: "32px",
                minHeight: "10px",
            },
        },
        muiTableCellProps: {
            sx: {
                fontSize: "0.875rem",
            },
        },
        muiTableHeadCellProps: {
            sx: {
                color: clientTheme.activeTextColor,
                backgroundColor: clientTheme.activeBg,
                fontWeight: "bold",
                padding: "2px 4px",
            }
        }
    });

    const handleExportToExcel = () => {
        if (onExportToExcel) {
            onExportToExcel(data, columns, name);
        }
    };

    return (
        <Box sx={{ position: 'relative', zIndex: 0 }}>
            {/* <Button variant="contained"  onClick={handleExportToExcel} sx={{ marginBottom: '10px', background:"#D1EEDC", color:"black", fontWeight:"bold" }}>
                Download Excel
            </Button> */}
            <MaterialReactTable table={table} />
        </Box>
    );
};

MaterialReactTableWithSearch.propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    enableRowActions: PropTypes.bool,
    renderRowActions: PropTypes.func,
    name: PropTypes.string,
    enableRowSelection: PropTypes.bool,
    renderTopToolbarCustomActions: PropTypes.func,
    enableSelectAll: PropTypes.bool,
    onExportToExcel: PropTypes.func, // Add the new prop type
    manualPagination: PropTypes.bool,
    rowCount: PropTypes.number,
    pagination: PropTypes.object,
    onPaginationChange: PropTypes.func,
    isLoading: PropTypes.bool,
};

MaterialReactTableWithSearch.defaultProps = {
    enableRowActions: false,
    renderRowActions: null,
    name: '',
    enableRowSelection: false,
    renderTopToolbarCustomActions: null,
    enableSelectAll: false,
    onExportToExcel: null, // Default to null if not provided
    manualPagination: false,
    rowCount: 0,
    pagination: undefined,
    onPaginationChange: null,
    isLoading: undefined,
};

export default MaterialReactTableWithSearch;
